import React from 'react';
import {
  Box,
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  LinearProgress,
} from '@mui/material';
import OnboardingHeader from './OnboardingHeader';

const ONBOARDING_STEPS = [
  { key: 'welcome', label: 'Welcome', required: true },
  { key: 'billing', label: 'Billing', required: true },
  { key: 'complete', label: 'Complete', required: true },
];

export default function OnboardingLayout({ 
  children, 
  currentStep, 
  completedSteps = [], 
  skippedSteps = [],
  title,
  subtitle 
}) {
  const currentStepIndex = ONBOARDING_STEPS.findIndex(step => step.key === currentStep);
  const progressPercentage = ((currentStepIndex + 1) / ONBOARDING_STEPS.length) * 100;

  const getStepStatus = (stepKey) => {
    if (completedSteps.includes(stepKey)) return 'completed';
    if (skippedSteps.includes(stepKey)) return 'skipped';
    if (stepKey === currentStep) return 'active';
    return 'pending';
  };

  return (
    <>
      <OnboardingHeader />
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: 'grey.50',
        py: 4 
      }}>
        <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Welcome to Meeting Rooms
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Let's get your workspace set up in just a few steps
          </Typography>
          
          {/* Progress Bar */}
          <Box sx={{ mt: 3, mb: 2 }}>
            <LinearProgress 
              variant="determinate" 
              value={progressPercentage} 
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Step {currentStepIndex + 1} of {ONBOARDING_STEPS.length}
            </Typography>
          </Box>
        </Box>

        {/* Step Indicator */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Stepper activeStep={currentStepIndex} alternativeLabel>
            {ONBOARDING_STEPS.map((step, index) => {
              const status = getStepStatus(step.key);
              const isCompleted = status === 'completed';
              const isSkipped = status === 'skipped';
              const isActive = status === 'active';
              
              return (
                <Step 
                  key={step.key} 
                  completed={isCompleted}
                  sx={{
                    '& .MuiStepLabel-root': {
                      color: isSkipped ? 'text.disabled' : 'inherit'
                    },
                    '& .MuiStepIcon-root': {
                      color: isCompleted ? 'success.main' : 
                             isSkipped ? 'grey.400' :
                             isActive ? 'primary.main' : 'grey.300',
                      '& .MuiStepIcon-text': {
                        fill: isCompleted ? 'white' : 
                              isActive ? 'white' : 'grey.600'
                      }
                    }
                  }}
                >
                  <StepLabel 
                    optional={
                      isSkipped ? (
                        <Typography variant="caption" color="text.disabled">
                          Skipped
                        </Typography>
                      ) : !step.required ? (
                        <Typography variant="caption" color="text.secondary">
                          Optional
                        </Typography>
                      ) : null
                    }
                  >
                    {step.label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Paper>

        {/* Main Content */}
        <Paper sx={{ p: 4, mb: 4 }}>
          {title && (
            <Typography variant="h4" component="h2" gutterBottom>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {subtitle}
            </Typography>
          )}
          
          {children}
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Need help? Contact our support team anytime.
          </Typography>
        </Box>
      </Container>
    </Box>
    </>
  );
} 